<template>
  <div class="page-planification">
    <landing-header />

    <main>
      <section class="hero-planning">
        <div class="max-w-7xl mx-auto px-4 flex flex-col lg:flex-row items-center gap-16">
          <div class="lg:w-1/2">
            <span class="badge-planning">Optimisation Logistique</span>
            <h1 class="text-4xl md:text-6xl font-black text-gray-900 mb-6 leading-tight">
              Des tournées <br />
              <span class="gradient-text">réalistes et rentables</span>
            </h1>
            <p class="text-xl text-gray-600 mb-8 leading-relaxed">
              Ne planifiez plus au hasard. Termoflow intègre les temps de trajet réels et les contraintes de vos techniciens pour des journées optimisées, sans imprévus.
            </p>
            <div class="flex flex-wrap gap-4">
              <a href="/contact"><button class="btn-primary">Demander une démonstration</button></a>
              <a href="/fonctionnalites" class="btn-outline-large">Autres fonctionnalités</a>
            </div>
          </div>
          
          <div class="lg:w-1/2 w-full">
            <div class="planning-visual shadow-2xl">
              <div class="calendar-header">
                <div class="day active">Lundi 26</div>
                <div class="day">Mardi 27</div>
                <div class="day">Mercredi 28</div>
              </div>
              <div class="calendar-grid">
                <div class="tech-row">
                  <div class="tech-name">Thomas D.</div>
                  <div class="slot-container">
                    <div class="event blue">Intervention #801</div>
                    <div class="travel-line"><span>15 min de route</span></div>
                    <div class="event orange">Dépannage PAC</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section class="py-24 bg-gray-900 text-white overflow-hidden relative">
        <div class="max-w-7xl mx-auto px-4">
          <div class="flex flex-col lg:flex-row items-center gap-16">
            <div class="lg:w-1/2">
              <h2 class="text-3xl font-bold mb-6">Calcul automatique des temps de trajet</h2>
              <p class="text-gray-400 mb-8">Fini les approximations. Termoflow calcule la route entre chaque intervention selon les adresses réelles et les points de départ (domicile ou dépôt).</p>
              <div class="route-box">
                <div class="route-step">🏠 Dépôt Arras</div>
                <div class="route-line"><i class="fa-solid fa-truck"></i> 12 km - 18 min</div>
                <div class="route-step">📍 Client : Résidence du Parc</div>
              </div>
            </div>
            <div class="lg:w-1/2 grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div class="feature-dark-card">
                <i class="fa-solid fa-map-location-dot"></i>
                <h4>Estimation fiable</h4>
                <p>Basée sur les adresses réelles des sites.</p>
              </div>
              <div class="feature-dark-card">
                <i class="fa-solid fa-house-user"></i>
                <h4>Départ/Arrivée</h4>
                <p>Gère les domiciles techniciens ou l'agence.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section class="py-24 bg-white">
        <div class="max-w-7xl mx-auto px-4">
          <div class="text-center mb-20">
            <h2 class="text-3xl font-bold text-gray-900 mb-4">Une planification pensée pour le terrain</h2>
            <p class="text-gray-600">Le planning devient un véritable outil de coordination, pas un simple agenda.</p>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-3 gap-12">
            <div class="plan-card">
              <div class="icon-wrap"><i class="fa-solid fa-calendar-day"></i></div>
              <h3>Vision par technicien</h3>
              <p>Visualisez la charge de travail de chaque membre de l'équipe en un coup d'œil.</p>
            </div>
            <div class="plan-card">
              <div class="icon-wrap"><i class="fa-solid fa-mobile-screen"></i></div>
              <h3>Accès Mobile</h3>
              <p>Les techniciens reçoivent leur planning mis à jour en temps réel sur leur application.</p>
            </div>
            <div class="plan-card">
              <div class="icon-wrap"><i class="fa-solid fa-chart-pie"></i></div>
              <h3>Pilotage & Data</h3>
              <p>Analysez le temps réellement consacré aux déplacements vs interventions.</p>
            </div>
          </div>
        </div>
      </section>

      <section class="py-24 bg-gray-50 border-y border-gray-100">
        <div class="max-w-7xl mx-auto px-4">
          <div class="bg-gradient-to-br from-orange-500 to-red-600 rounded-[3rem] p-12 text-white">
            <div class="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 class="text-3xl font-bold mb-8">Bénéfices concrets</h2>
                <ul class="benefit-list-white">
                  <li><i class="fa-solid fa-check"></i> Planification plus réaliste</li>
                  <li><i class="fa-solid fa-check"></i> Meilleure anticipation des déplacements</li>
                  <li><i class="fa-solid fa-check"></i> Réduction des retards et des imprévus</li>
                  <li><i class="fa-solid fa-check"></i> Meilleure coordination des équipes</li>
                </ul>
              </div>
              <div class="text-center lg:text-right">
                <div class="text-6xl font-black mb-2">-25%</div>
                <p class="text-xl opacity-90 uppercase tracking-widest font-bold">de temps de route inutile</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section class="py-20 text-center">
        <div class="max-w-3xl mx-auto px-4">
          <h2 class="text-4xl font-black text-gray-900 mb-8">Optimisez vos tournées dès demain</h2>
          <div class="flex flex-col sm:flex-row justify-center gap-4">
             <a href="/contact"><button class="btn-primary-large">Demander une démonstration</button></a>
             <a href="/fonctionnalites" class="btn-outline-large">Autres fonctionnalités</a>
          </div>
        </div>
      </section>

      <landing-roi />
      <landing-avis />
    </main>

    <landing-footer />
  </div>
</template>

<script>
import LandingHeader from './LandingHeader.vue'
import LandingFooter from './LandingFooter.vue'
import LandingAvis from './LandingAvis.vue'
import LandingROI from './LandingROI.vue'

export default {
  name: 'PlanningPage',
  components: {
    LandingHeader,
    LandingFooter,
    LandingAvis,
    'landing-roi': LandingROI
  }
}
</script>

<style scoped>
/* --- UTILS --- */
.gradient-text {
  background: linear-gradient(to right, #f97316, #ef4444);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

/* --- HERO --- */
.hero-planning {
  padding: 10rem 0 6rem;
  background: radial-gradient(circle at top right, #fff7ed, transparent 35%);
}

.badge-planning {
  display: inline-block;
  padding: 0.5rem 1rem;
  background-color: #ffedd5;
  color: #c2410c;
  font-weight: 800;
  font-size: 0.75rem;
  border-radius: 9999px;
  margin-bottom: 1.5rem;
  text-transform: uppercase;
}

/* --- VISUEL PLANNING --- */
.planning-visual {
  background: white;
  border-radius: 2rem;
  overflow: hidden;
  border: 1px solid #e5e7eb;
}
.calendar-header { display: flex; background: #f9fafb; border-bottom: 1px solid #e5e7eb; }
.day { padding: 1rem; flex: 1; text-align: center; font-size: 0.8rem; font-weight: 700; color: #9ca3af; }
.day.active { color: #f97316; border-bottom: 3px solid #f97316; background: white; }

.calendar-grid { padding: 2rem; }
.tech-row { display: flex; align-items: center; gap: 1.5rem; }
.tech-name { font-weight: 800; font-size: 0.9rem; width: 80px; }
.slot-container { flex: 1; display: flex; flex-direction: column; gap: 0.5rem; }
.event { padding: 0.75rem; border-radius: 0.5rem; color: white; font-weight: 700; font-size: 0.75rem; }
.event.blue { background: #3b82f6; }
.event.orange { background: #f97316; }
.travel-line { 
  border-left: 2px dashed #d1d5db;
  margin-left: 1rem;
  padding: 0.5rem 1rem;
  font-size: 0.65rem;
  color: #9ca3af;
  font-style: italic;
}

/* --- SECTION DARK --- */
.route-box { background: #1f2937; padding: 2rem; border-radius: 1.5rem; border: 1px solid #374151; }
.route-step { font-weight: 800; font-size: 1rem; }
.route-line { padding: 1rem 2rem; color: #f97316; font-weight: 700; font-size: 0.85rem; display: flex; align-items: center; gap: 1rem; }
.feature-dark-card { background: #1f2937; padding: 2rem; border-radius: 1.5rem; text-align: center; border: 1px solid #374151; }
.feature-dark-card i { font-size: 2rem; color: #f97316; margin-bottom: 1rem; }
.feature-dark-card h4 { font-weight: 800; margin-bottom: 0.5rem; }
.feature-dark-card p { font-size: 0.85rem; color: #9ca3af; }

/* --- CARDS --- */
.plan-card { text-align: center; padding: 2rem; }
.icon-wrap { width: 4rem; height: 4rem; background: #fff7ed; color: #f97316; border-radius: 1rem; display: flex; align-items: center; justify-content: center; font-size: 1.5rem; margin: 0 auto 1.5rem; }
.plan-card h3 { font-weight: 900; margin-bottom: 1rem; font-size: 1.25rem; }
.plan-card p { color: #6b7280; font-size: 0.95rem; line-height: 1.6; }

/* --- LISTS --- */
.benefit-list-white { list-style: none; padding: 0; }
.benefit-list-white li { display: flex; align-items: center; gap: 1rem; font-weight: 700; font-size: 1.1rem; margin-bottom: 1rem; }

/* --- BUTTONS --- */
.btn-primary { background: linear-gradient(to right, #f97316, #ef4444); color: white; padding: 1rem 2.5rem; border-radius: 9999px; font-weight: 700; border: none; }
.btn-primary-large { background: #f97316; color: white; padding: 1.2rem 3rem; border-radius: 9999px; font-weight: 800; border: none; font-size: 1.1rem; }
.btn-outline-large { border: 2px solid #e5e7eb; padding: 1.2rem 3rem; border-radius: 9999px; font-weight: 800; color: #1f2937; text-decoration: none; display: inline-block; }

@media (max-width: 1024px) {
  .hero-planning { text-align: center; padding-top: 8rem; }
  .hero-planning .flex { flex-direction: column; }
  .planning-visual { margin-top: 4rem; }
}
</style>